import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import IconSystem from 'icon-collection/icon-systems';
import { ModuleHeader } from 'components/modals';
import type { ActionButton } from 'components/modals/common/ModuleHeader';
import notificationService from 'services/notificationService';
import { dineOpsService } from 'api/services/dineOpsService';
import { STORAGE_KEYS } from 'auth/storageKeys';
import { printKot } from './kotPrint';
import { PrintAlertBanner, Chip, canSeeModule, currentOutletId, PRINT_MODULES } from 'components/modules/print-module/printUi';
import PrintReasonModal from 'components/modals/modules/print-module/PrintReasonModal';
import type { DineKot, DineKotLine, DineKitchenStation, DineOutlet } from 'api/types';
import './kitchenDisplay.css';

/**
 * KDS-001 — the kitchen display (plan §5.2, mockup kitchen-display.html), in
 * the app's own look.
 *
 * Two ways in, one board:
 *  - mode "tab": a Restaurant screen inside the app, with the standard page
 *    header (title, description, refresh, full screen), like every other screen.
 *  - mode "wall": the same board alone in the window at /kitchen-display, for
 *    the kitchen TV. /kitchen-display?dept=BAKERY locks it to one department
 *    (the department code): its name large, no department buttons to mis-tap.
 *
 * Tickets of the chosen department, oldest first, coloured by state, elapsed
 * against the ticket's own target. The kitchen's actions are Accept, Ready
 * (whole ticket or line by line) and Reject with a reason, for roles that hold
 * update / reject on dineops-kds. Served is the waiter's word, from the waiter
 * app; a served ticket leaves the board. Without a ?dept= link the board
 * remembers the last department chosen on this browser; the buttons are the
 * departments this outlet sends to.
 *
 * Approved blueprint deviation: a queue of cards, not a data grid. No
 * pagination, filter panel or export. It polls the live feed every ten seconds.
 */

const POLL_MS = 10_000;
const MODULE = 'dineops-kds';
const REJECT_REASONS = ['Out of stock', 'Ingredient unavailable', 'Equipment down', 'Cannot meet the instruction'];
/** Why a ticket printed by the agent is printed again (BR-PRN-005: reprints are deliberate and audited). */
const REPRINT_REASONS = ['Paper jam', 'Lost ticket', 'Unreadable'];

/** The print chip of an agent-printed ticket; nothing for a browser-printed one (D-P10). */
const PRINT_CHIP: Record<string, { label: string; tone: 'ok' | 'info' | 'warn' | 'danger' | 'muted' }> = {
  QUEUED: { label: 'Queued', tone: 'info' }, PRINTING: { label: 'Printing', tone: 'info' }, SENT: { label: 'Sent', tone: 'ok' },
  PRINTED: { label: 'Printed', tone: 'ok' }, FAILED: { label: 'Print failed', tone: 'danger' }, CANCELLED: { label: 'Print cancelled', tone: 'muted' },
};
const printChip = (k: DineKot) => {
  const p = k.print;
  if (!p || p.mode !== 'AGENT' || !p.status) return null;
  const c = PRINT_CHIP[p.status] ?? { label: p.status, tone: 'muted' as const };
  const label = p.rerouted && p.status !== 'FAILED' ? `Rerouted to ${p.printerName ?? 'backup'}` : c.label;
  const tone = p.rerouted && p.status !== 'FAILED' ? 'warn' : c.tone;
  return (
    <span data-kds-print={p.status}>
      <Chip tone={tone} title={p.error ?? (p.printerName ? `${label} · ${p.printerName}` : undefined)}>{label}{p.reprintCount ? ` · R${p.reprintCount}` : ''}</Chip>
    </span>
  );
};

const pad = (n: number) => String(n).padStart(2, '0');

/** The last department chosen on this browser (by code); a convenience, so failures are ignored. */
const DEPT_KEY = 'kds_department';
const readDept = () => { try { return localStorage.getItem(DEPT_KEY) ?? ''; } catch { return ''; } };
const writeDept = (code: string) => { try { if (code) localStorage.setItem(DEPT_KEY, code); else localStorage.removeItem(DEPT_KEY); } catch { /* private window */ } };

/** The kitchen actions this user holds, from sign-in, for the wall page (a tab is handed them). */
const storedActions = (): string[] | null => {
  try {
    const all = JSON.parse(localStorage.getItem(STORAGE_KEYS.RBAC_MODULE_ACTIONS) || 'null');
    return all && Array.isArray(all[MODULE]) ? all[MODULE] : null;
  } catch { return null; }
};

/** ok / warn / late against the ticket's own target (15 min when none). */
const ageClass = (elapsed: number, target?: number | null) => {
  const t = target && target > 0 ? target : 15;
  if (elapsed >= t) return 'late';
  if (elapsed >= Math.ceil(t * 0.6)) return 'warn';
  return 'ok';
};

const STATE_LABEL: Record<string, string> = { NEW: 'New', ACCEPTED: 'Accepted', PREPARING: 'Preparing', READY: 'Ready' };

interface Props {
  /** "tab" inside the app (default for the Restaurant menu entry), "wall" alone in the window. */
  mode?: 'tab' | 'wall';
  allowedActions?: string[];
  /** Opens another module's tab (the Print Queue, from the print-trouble banner). */
  onNavigateModule?: (moduleId: string) => void;
}

const KitchenDisplayScreen: React.FC<Props> = ({ mode = 'wall', allowedActions, onNavigateModule }) => {
  const wall = mode === 'wall';
  const [outlet, setOutlet] = useState<DineOutlet | null>(null);
  const [stations, setStations] = useState<DineKitchenStation[]>([]);
  const [allowedIds, setAllowedIds] = useState<Set<string> | null>(null);
  /** The departments this user may work (Role Permissions > Restaurant tab); null = every department. */
  const [mine, setMine] = useState<Set<string> | null>(null);
  const [kots, setKots] = useState<DineKot[]>([]);
  const [params, setParams] = useSearchParams();
  const lockCode = wall ? (params.get('dept') ?? '').trim().toUpperCase() : '';
  const [chosenCode, setChosenCode] = useState<string>(readDept);   // '' = all
  const [clock, setClock] = useState('');
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [rejecting, setRejecting] = useState<DineKot | null>(null);
  const [reason, setReason] = useState(REJECT_REASONS[0]);
  const [reasonText, setReasonText] = useState('');
  const [reprinting, setReprinting] = useState<DineKot | null>(null);
  const [reprintBusy, setReprintBusy] = useState(false);
  const [reprintError, setReprintError] = useState<string | null>(null);
  const timer = useRef<number | null>(null);

  // What this user may do here. Unknown (an old sign-in) hides the actions — fail-closed,
  // as every crs-web screen; signing in again loads them.
  const actions = useMemo(() => allowedActions ?? storedActions() ?? [], [allowedActions]);
  const canWork = actions.includes('update') || actions.includes('manage');
  const canReject = actions.includes('reject') || actions.includes('manage');
  // Reprint through the agent (BR-PRN-005): the print action on dineops-kds (crs-db fixes/060).
  const canReprint = actions.includes('print') || actions.includes('manage');

  const tick = useCallback(() => {
    const d = new Date();
    setClock(`${pad(d.getHours())}:${pad(d.getMinutes())}`);
  }, []);

  const load = useCallback(async () => {
    try {
      // The feed is every live ticket; the department filter is applied here so
      // the counts can be shown for every department at once.
      setKots(await dineOpsService.getKots());
      setFailed(false);
    } catch {
      setFailed(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    tick();
    dineOpsService.getOutlet().then((o) => {
      setOutlet(o);
      // The departments this outlet sends to; if they cannot be read, every department shows.
      dineOpsService.getOutletStations(o.dineOutletId)
        .then((list) => setAllowedIds(new Set(list.filter((x) => x.isAllowed).map((x) => x.stationId))))
        .catch(() => undefined);
    }).catch(() => undefined);
    dineOpsService.getStations({ page: 1, pageSize: 50 }).then((r) => setStations(r.data ?? [])).catch(() => undefined);
    // The API already sends only these departments' tickets and refuses work on others; this keeps the buttons in step.
    dineOpsService.getMyStations().then((m) => setMine(m.all ? null : new Set(m.stationIds))).catch(() => undefined);
    void load();
    timer.current = window.setInterval(() => { void load(); tick(); }, POLL_MS);
    return () => { if (timer.current) window.clearInterval(timer.current); };
  }, [load, tick]);

  /** Replace one ticket in place after an action, or drop it when it has left the live feed. */
  const patch = useCallback((k: DineKot) => {
    setKots((p) => {
      const live = k.kotStatus !== 'SERVED' && k.kotStatus !== 'CANCELLED';
      const without = p.filter((x) => x.id !== k.id);
      return live ? [...without, k] : without;
    });
  }, []);

  const act = useCallback(async (k: DineKot, fn: () => Promise<DineKot>, done: string) => {
    setBusyId(k.id);
    try {
      patch(await fn());
      notificationService.toast.success(`${k.kotNumber} ${done}`);
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message ?? 'The kitchen action failed.');
      void load();
    } finally {
      setBusyId(null);
    }
  }, [patch, load]);

  const accept = (k: DineKot) => act(k, () => dineOpsService.acceptKot(k.id), 'accepted');
  // DO-308: the ticket on paper, from the API's print payload. The browser's print dialog reaches
  // whatever printer this department's screen has; per-department routing is the payload's printerName.
  const printTicket = async (k: DineKot) => {
    try { const p = await dineOpsService.getKotPrint(k.id); if (!printKot(p)) notificationService.toast.error('Allow popups for this screen to print tickets.'); }
    catch { notificationService.toast.error('The ticket could not be loaded for printing.'); }
  };
  const ready = (k: DineKot) => act(k, () => dineOpsService.readyKot(k.id), 'ready — the waiter is told');
  // An agent-printed ticket prints again through the agent, marked REPRINT n, with a reason.
  const reprint = async (why: string) => {
    if (!reprinting) return;
    const k = reprinting;
    setReprintBusy(true); setReprintError(null);
    try {
      const r = await dineOpsService.reprintKot(k.id, why);
      setKots((p) => p.map((x) => (x.id === k.id ? { ...x, print: r?.summary ?? x.print } : x)));
      notificationService.toast.success(`${k.kotNumber} sent to print again${r?.summary?.printerName ? ` at ${r.summary.printerName}` : ''}.`);
      setReprinting(null);
    } catch (err: any) {
      setReprintError(err?.response?.data?.message ?? 'The ticket could not be reprinted.');
    } finally { setReprintBusy(false); }
  };
  const lineReady = (k: DineKot, l: DineKotLine) => {
    if (!canWork) return;
    if (k.kotStatus === 'NEW') { notificationService.toast.info('Accept the ticket, then tick its lines as they are ready.'); return; }
    if (l.lineStatus === 'READY' || l.lineStatus === 'SERVED' || l.lineStatus === 'CANCELLED') return;
    void act(k, () => dineOpsService.kotLineReady(k.id, l.id), `· ${l.itemName} ready`);
  };
  const reject = async () => {
    if (!rejecting) return;
    const why = reason === 'Other' ? reasonText.trim() : reason;
    if (!why) { notificationService.toast.error('Say why the ticket is rejected.'); return; }
    const k = rejecting; setRejecting(null); setReasonText('');
    await act(k, () => dineOpsService.rejectKot(k.id, why), 'rejected — the lines go back to the waiter');
  };

  // The buttons: departments this outlet sends to, plus any that has a ticket on screen
  // (another outlet's, or a fallback), so no live ticket is ever without a button.
  const shownStations = useMemo(() => stations.filter((s) =>
    (!mine || mine.has(s.id))
    && (!allowedIds || allowedIds.size === 0 || allowedIds.has(s.id) || kots.some((k) => k.stationId === s.id))), [stations, allowedIds, kots, mine]);
  /** A cook with one department opens straight on it: no "All", no choice to make. */
  const onlyOne = mine && shownStations.length === 1 ? shownStations[0] : undefined;
  const noDepartments = !!mine && mine.size === 0;
  const locked = lockCode ? stations.find((s) => s.stationCode.toUpperCase() === lockCode) : undefined;
  const unknownLock = !!lockCode && stations.length > 0 && !locked;
  const chosen = chosenCode ? shownStations.find((s) => s.stationCode.toUpperCase() === chosenCode.toUpperCase()) : undefined;
  const station = locked?.id ?? chosen?.id ?? onlyOne?.id ?? '';
  const choose = (s?: DineKitchenStation) => { const code = s?.stationCode ?? ''; setChosenCode(code); writeDept(code); };
  const lockHere = () => { if (chosen) setParams({ dept: chosen.stationCode }); };
  const openWall = useCallback(() => {
    const url = `/kitchen-display${chosen ? `?dept=${encodeURIComponent(chosen.stationCode)}` : ''}`;
    window.open(url, '_blank', 'noopener');
  }, [chosen]);

  // Work first: NEW / ACCEPTED / PREPARING oldest first, then READY (waiting for the waiter).
  const shown = useMemo(() => kots
    .filter((k) => !station || k.stationId === station)
    .sort((a, b) => {
      const ra = a.kotStatus === 'READY' ? 1 : 0; const rb = b.kotStatus === 'READY' ? 1 : 0;
      if (ra !== rb) return ra - rb;
      return String(a.sentAt).localeCompare(String(b.sentAt));
    }), [kots, station]);

  const countFor = (id: string) => kots.filter((k) => (!id || k.stationId === id) && k.kotStatus !== 'READY').length;

  const actionButtons: ActionButton[] = useMemo(() => [
    { id: 'refresh', icon: <IconSystem.refresh size={18} />, label: '', onClick: () => void load(), tooltip: 'Refresh tickets', variant: 'icon' },
    { id: 'wall', icon: <IconSystem.windowRestore size={18} />, label: '', onClick: openWall,
      tooltip: chosen ? `Open ${chosen.stationName} full screen for the kitchen TV` : 'Open full screen for the kitchen TV', variant: 'icon' },
  ], [load, openWall, chosen]);

  const legend = (
    <div className="kds-legend">
      <span><i style={{ background: 'var(--info)' }} />New</span>
      <span><i style={{ background: 'var(--primary)' }} />Accepted</span>
      <span><i style={{ background: 'var(--warning)' }} />Preparing</span>
      <span><i style={{ background: 'var(--success)' }} />Ready</span>
      <span><i className="kds-swatch--room" />Room service</span>
      <span><i className="kds-swatch--guest" />Hotel guest</span>
      <span><i className="kds-swatch--vip" />VIP</span>
    </div>
  );

  const chips = !locked && (
    <div className="kds-chips" data-kds-chips>
      {!onlyOne && (
        <button type="button" className={`kds-chip${station === '' ? ' is-active' : ''}`} onClick={() => choose()}>
          All <span className="kds-chip__count">{countFor('')}</span>
        </button>
      )}
      {shownStations.map((s) => (
        <button type="button" key={s.id} className={`kds-chip${station === s.id ? ' is-active' : ''}`} onClick={() => choose(s)}
          title={s.isOpen === false ? (s.fallbackOn ? `Closed: dishes go to ${s.fallbackStationName}` : 'Closed: dishes unavailable') : undefined}>
          {s.stationName}{s.isOpen === false && <span className="kds-chip__closed">(closed)</span>}
          <span className="kds-chip__count">{countFor(s.id)}</span>
        </button>
      ))}
      {wall && chosen && (
        <button type="button" className="kds-lock" onClick={lockHere}
          title={`Opens /kitchen-display?dept=${chosen.stationCode}: only ${chosen.stationName}, no station buttons. Bookmark it on this screen.`}>
          Lock screen to {chosen.stationName}
        </button>
      )}
    </div>
  );

  const board = (
    <div className="kds-body">
      {loading && <div className="kds-empty">Loading tickets…</div>}
      {noDepartments && (
        <div className="kds-empty" data-kds-no-departments>You have no kitchen stations. Ask an administrator to give you one in Role Permissions (Restaurant tab).</div>
      )}
      {!noDepartments && locked && mine && !mine.has(locked.id) && (
        <div className="kds-empty" data-kds-not-mine>{locked.stationName} is not one of your kitchen stations, so its tickets are not shown here.</div>
      )}
      {!loading && !noDepartments && shown.length === 0 && (
        <div className="kds-empty">{station ? 'Nothing waiting at this station.' : 'Nothing waiting. Tickets appear here the moment a waiter sends them.'}</div>
      )}
      <div className="kds-grid">
        {shown.map((k) => {
          const isRoom = !!k.roomNumber && !k.tableNumber;
          // A hotel guest eating in the restaurant: at a table, but staying in a room.
          const isGuest = !isRoom && k.guestPresence === 'IN_HOUSE' && !!k.roomNumber;
          // A department's requisition (R1): no table, no bill; handed over from the Requisitions screen.
          const isRequisition = k.orderChannel === 'REQUISITION';
          const busy = busyId === k.id;
          return (
            <div key={k.id} data-kds-card
              className={`kds-card s-${k.kotStatus.toLowerCase()}${k.fallbackAlert ? ' is-alert' : ''}${isRoom ? ' is-room' : ''}${isGuest ? ' is-guest' : ''}${k.isVip ? ' is-vip' : ''}`}>
              {/* BR-058: a fallback ticket says which closed department it is covering for. */}
              {k.fallbackFromStationName && (
                <div className="kds-card__banner">
                  FALLBACK FROM {k.fallbackFromStationName.toUpperCase()}{k.fallbackAlert ? ' · NOT ACCEPTED YET' : ''}
                </div>
              )}
              {/* Room service, hotel guest and VIP: a band the chef sees from across the kitchen; it pulses until the ticket is accepted. */}
              {(isRoom || isGuest || isRequisition || k.isVip) && (
                <div className={`kds-flags${k.kotStatus === 'NEW' ? ' is-new' : ''}`}>
                  {isRequisition && <span className="kds-flag kds-flag--req" data-kds-flag="requisition">NC KOT - {(k.requisitionFor ?? '').toUpperCase()}</span>}
                  {isRoom && <span className="kds-flag kds-flag--room" data-kds-flag="room">ROOM SERVICE · ROOM {k.roomNumber}</span>}
                  {isGuest && <span className="kds-flag kds-flag--guest" data-kds-flag="guest">HOTEL GUEST · ROOM {k.roomNumber}</span>}
                  {k.isVip && <span className="kds-flag kds-flag--vip" data-kds-flag="vip">★ VIP</span>}
                </div>
              )}
              <div className="kds-card__head">
                <div>
                  <div className="kds-card__no">
                    {k.outletCode ? `${k.outletCode} · ` : ''}{k.kotNumber}
                    {k.kotType !== 'NEW' && <span className="kds-type">{k.kotType}</span>}
                  </div>
                  <div className="kds-card__meta">
                    {isRequisition ? <>NC · {[k.requisitionNumber, k.requisitionFor, k.requisitionPurpose].filter(Boolean).join(' · ')}</> : isRoom ? <>Room {k.roomNumber}</> : k.tableNumber ? <>Table {k.tableNumber}</> : <>Takeaway</>}
                    {' '}· {k.orderNumber}<br />
                    round {k.roundNumber} · {k.covers} cover{k.covers === 1 ? '' : 's'}{k.stationName && !station ? ` · ${k.stationName}` : ''}
                  </div>
                </div>
                <div className="kds-card__side">
                  <span className="kds-status">{STATE_LABEL[k.kotStatus] ?? k.kotStatus}</span>
                  <span className={`kds-age ${ageClass(k.elapsedMinutes, k.targetMinutes)}`} title={k.targetMinutes ? `target ${k.targetMinutes} min` : ''}>
                    {k.elapsedMinutes} min{k.targetMinutes ? ` / ${k.targetMinutes}` : ''}
                  </span>
                </div>
              </div>

              <div className="kds-card__lines">
                {k.lines.map((l) => {
                  const done = l.lineStatus === 'READY' || l.lineStatus === 'SERVED';
                  return (
                    <button type="button" key={l.id} className={`kds-line${done ? ' is-done' : ''}`}
                      disabled={busy || !canWork || k.kotStatus === 'READY'} onClick={() => lineReady(k, l)}
                      title={!canWork ? '' : k.kotStatus === 'NEW' ? 'Accept the ticket first' : done ? 'Ready' : 'Mark this line ready'}>
                      <span className="kds-line__check">✓</span>
                      <span className="kds-line__name">
                        {l.itemName}{l.variantName ? ` (${l.variantName})` : ''}
                        {l.instructions && l.instructions.split(';').map((m, i) => m.trim() && <div key={i} className="kds-line__mods">{m.trim()}</div>)}
                      </span>
                      <span className="kds-line__qty">×{l.quantity}</span>
                    </button>
                  );
                })}
              </div>

              {printChip(k) && <div className="kds-card__print">{printChip(k)}</div>}
              <div className="kds-card__foot">
                {k.print?.mode === 'AGENT' && canReprint && (
                  <button type="button" className="btn btn-secondary kds-narrow" disabled={busy} data-kds-reprint
                    title="Print again through the printer, marked REPRINT" onClick={() => { setReprintError(null); setReprinting(k); }}>Reprint</button>
                )}
                <button type="button" className="btn btn-secondary kds-narrow" disabled={busy} data-kds-browser-print
                  title={k.print?.mode === 'AGENT' ? 'Print on this screen (browser)' : 'Print the ticket (DO-308)'} onClick={() => void printTicket(k)}>
                  <IconSystem.print size={14} />
                </button>
                {canWork && k.kotStatus === 'NEW' && <button type="button" className="btn btn-primary" disabled={busy} onClick={() => void accept(k)}>Accept</button>}
                {canWork && (k.kotStatus === 'ACCEPTED' || k.kotStatus === 'PREPARING') && <button type="button" className="btn btn-success" disabled={busy} onClick={() => void ready(k)}>Mark ready</button>}
                {k.kotStatus === 'READY' && <button type="button" className="btn btn-secondary" disabled>Waiting for the waiter</button>}
                {canReject && k.kotStatus !== 'READY' && (
                  <button type="button" className="btn btn-secondary kds-narrow" disabled={busy} onClick={() => setRejecting(k)}>Reject</button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );

  const rejectDialog = rejecting && (
    <>
      <div className="modal-backdrop" onClick={() => setRejecting(null)} />
      <div className="modal-container modal-sm">
        <div className="modal-header">
          <h2 className="modal-title">Reject {rejecting.kotNumber}</h2>
          <button className="modal-close" onClick={() => setRejecting(null)}><IconSystem.x size={18} /></button>
        </div>
        <div className="modal-content">
          <p style={{ marginTop: 0, fontSize: 'var(--font-size-sm)', opacity: 0.8 }}>
            The ticket is cancelled and its lines go back to the waiter as unsent, with your reason. A reason is required.
          </p>
          <div className="form-group">
            <label className="form-label">Reason <span className="text-danger">*</span></label>
            <select className="form-input" value={reason} onChange={(e) => setReason(e.target.value)}>
              {REJECT_REASONS.map((r) => <option key={r} value={r}>{r}</option>)}
              <option value="Other">Other…</option>
            </select>
          </div>
          {reason === 'Other' && (
            <div className="form-group">
              <input className="form-input" value={reasonText} onChange={(e) => setReasonText(e.target.value)} placeholder="Say why" maxLength={200} autoFocus />
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={() => setRejecting(null)}>Cancel</button>
          <button className="btn btn-danger" onClick={() => void reject()}>Reject ticket</button>
        </div>
      </div>
    </>
  );

  const reprintDialog = (
    <PrintReasonModal isOpen={!!reprinting} title={`Reprint ${reprinting?.kotNumber ?? ''}`}
      message={`Prints again at ${reprinting?.print?.printerName ?? 'the station printer'}, marked REPRINT. A reason is required and recorded.`}
      confirmLabel="Reprint" presets={REPRINT_REASONS} busy={reprintBusy} error={reprintError}
      footerExtra={reprinting && (
        <button type="button" className="btn btn-secondary" style={{ marginTop: 8 }} data-kds-reprint-browser
          onClick={() => { const k = reprinting; setReprinting(null); void printTicket(k); }}>Print on this screen instead</button>
      )}
      onConfirm={(why) => void reprint(why)} onCancel={() => setReprinting(null)} />
  );

  // Print trouble at this outlet (BR-PRN-002): a banner, never a toast.
  const printBanner = (
    <PrintAlertBanner scopeId={currentOutletId() || outlet?.dineOutletId}
      onOpenQueue={onNavigateModule && canSeeModule(PRINT_MODULES.QUEUE) ? () => onNavigateModule(PRINT_MODULES.QUEUE) : undefined} />
  );

  const status = (
    <>
      {outlet?.outletName ?? '…'} · business date {outlet?.businessDate ?? '…'} · {clock}
      {locked && <> · {countFor(locked.id)} waiting</>}
      {failed && <span className="kds-bar__error">— feed unavailable, retrying</span>}
      {unknownLock && <span className="kds-bar__error" data-kds-unknown>— no kitchen station with code {lockCode}; showing all</span>}
    </>
  );

  if (wall) {
    return (
      <div className="kds kds--wall" data-kds-mode="wall">
        <div className="kds-bar">
          <div className="kds-bar__title">
            {locked
              ? <h1 className="is-department" data-kds-department>{locked.stationName}{locked.isOpen === false && <span className="kds-bar__error" style={{ fontSize: '1rem' }}>(closed)</span>}</h1>
              : <h1>Kitchen Display</h1>}
            <div className="kds-bar__sub">{locked ? 'Kitchen Display · ' : ''}{status}</div>
          </div>
          {legend}
        </div>
        {!locked && <div className="kds-toolbar">{chips}</div>}
        {printBanner}
        {board}
        {rejectDialog}
        {reprintDialog}
      </div>
    );
  }

  return (
    <div className="module-master-container" data-kds-mode="tab">
      <ModuleHeader
        title="Kitchen Display"
        description={`Tickets for ${outlet?.outletName ?? 'this outlet'}'s kitchen stations · business date ${outlet?.businessDate ?? '…'} · ${clock}`}
        icon={<IconSystem.list size={18} />}
        actions={actionButtons}
      />
      <div className="module-content kds" style={{ marginTop: '6px' }}>
        <div className="kds-toolbar">
          {chips}
          {failed && <span className="kds-bar__error">Feed unavailable, retrying</span>}
          {legend}
        </div>
        {printBanner}
        {board}
      </div>
      {rejectDialog}
      {reprintDialog}
    </div>
  );
};

export default KitchenDisplayScreen;

/** The Restaurant menu entry: the kitchen display inside the app. */
export const DineOpsKitchenDisplayScreen: React.FC<{ allowedActions?: string[]; onNavigateModule?: (moduleId: string) => void }> = ({ allowedActions, onNavigateModule }) => (
  <KitchenDisplayScreen mode="tab" allowedActions={allowedActions} onNavigateModule={onNavigateModule} />
);
